/**
 * The one place the web app talks to the iOS and Android wrappers.
 *
 * The contract is docs/native-bridge.md. Before it, four different checks
 * decided "am I embedded?", and the callbacks were named differently on each
 * platform (`onOneSignalPlayerId` on iOS, `onOneSignalPlayerIdReceived` on
 * Android), so every screen that wanted the camera wired up its own globals.
 * Here a screen asks for a capability, and gets the wrapper's version of it
 * under bridge v2 or the browser's own when there is no wrapper: a file input,
 * the share sheet or the clipboard, a new tab, the Notification API.
 *
 * Only bridge v2 lives here. The old shell keeps its v1 globals until the
 * plan's cleanup step deletes it with them.
 */
import { canOpenCamera } from '@relay-towing/ui';
import { isEmbeddedMode } from '../utils/embeddedMode';

export type NativePlatform = 'ios' | 'android';

/** One photo or video the wrapper took or picked. */
export interface MediaItem {
  /** The file's bytes, base64, with no `data:` prefix. */
  base64: string;
  mimeType: string;
  lat?: number;
  lng?: number;
  /** Sent for camera captures, not for library picks. */
  capturedAt?: string;
}

export interface SkippedMedia {
  mimeType?: string;
  bytes?: number;
  reason: 'tooLarge' | 'unreadable' | 'limit';
}

/** What the wrapper sends the page, through `RelayNative.receive`. */
export type NativeMessage =
  | { type: 'token'; data: { accessToken: string } }
  | { type: 'signedOut'; data?: undefined }
  | { type: 'signInFailed'; data: { cancelled: boolean; message?: string } }
  | { type: 'media'; data: { items: MediaItem[] } }
  | { type: 'mediaComplete'; data: { count: number; skipped: SkippedMedia[] } }
  | { type: 'mediaCancelled'; data?: undefined }
  | { type: 'qrScanned'; data: { code: string } }
  | { type: 'scanCancelled'; data?: undefined }
  | { type: 'notificationPermission'; data: { granted: boolean } }
  | { type: 'pushPlayerId'; data: { id: string | null } }
  | { type: 'navigate'; data: { path: string } };

export type NativeMessageType = NativeMessage['type'];

/** How to sign in: Auth0's page, straight to Google or Apple, or for one email. */
export interface NativeSignIn {
  connection?: 'google-oauth2' | 'apple';
  loginHint?: string;
}

/** What the page sends the wrapper, through `RelayNative.send`. */
export type WebMessage =
  | { type: 'signIn'; data: NativeSignIn }
  | { type: 'signOut' }
  | { type: 'requestTokenRefresh' }
  | { type: 'openMediaPicker'; data: { allowVideo: boolean } }
  | { type: 'capturePhoto' }
  | { type: 'openScanner' }
  | { type: 'share'; data: { title?: string; text?: string; url?: string } }
  | { type: 'openExternal'; data: { url: string } }
  | { type: 'getNotificationPermission' }
  | { type: 'requestNotificationPermission' }
  | { type: 'sendTestNotification' }
  | { type: 'openAppSettings' }
  | { type: 'setCompany'; data: { companyId: string } };

export interface RelayNative {
  platform: NativePlatform;
  appVersion: string;
  bridgeVersion: number;
  send(message: { type: string; data?: object }): void;
  receive?: (message: { type: string; data?: object }) => void;
}

declare global {
  interface Window {
    RelayNative?: RelayNative;
  }
}

/** The wrapper, when it speaks bridge v2; null in a browser or an older build. */
export function native(): RelayNative | null {
  if (typeof window === 'undefined') return null;
  const relay = window.RelayNative;
  return relay && relay.bridgeVersion >= 2 ? relay : null;
}

/** Inside a wrapper that speaks docs/native-bridge.md. */
export const isNative = (): boolean => native() !== null;

/**
 * Inside a wrapper built before bridge v2. Those have no `RelayNative`, only
 * the v1 globals or the `?embedded=true` they were opened with, and they get
 * the old shell until the minimum version passes them.
 */
export function isLegacyWrapper(): boolean {
  return hasV1Bridge() || (!isNative() && isEmbeddedMode());
}

/**
 * A wrapper built before bridge v2, known by the v1 global both put on the
 * page (iOS injects it, Android adds it as an interface), which a browser
 * never has. Stricter than isLegacyWrapper, which also trusts
 * `?embedded=true` and keeps it in storage: what stops a build or opens a
 * link in place must not happen to a browser that once had the flag.
 */
export function hasV1Bridge(): boolean {
  if (isNative() || typeof window === 'undefined') return false;
  return Boolean(window.ReactNativeWebView);
}

/**
 * Which phone a wrapper built before v2 runs on. Those builds do not say,
 * so this reads the user agent: Android's names itself, and anything else
 * is an iPhone or iPad, the only other wrapper there is.
 */
export function legacyPlatform(): NativePlatform {
  return /android/i.test(navigator.userAgent) ? 'android' : 'ios';
}

/**
 * Tell the wrapper something. False when there is no v2 wrapper to tell, so
 * the caller can do the browser's version instead.
 */
export function send(message: WebMessage): boolean {
  const relay = native();
  if (!relay) return false;
  relay.send(message);
  return true;
}

// ─── Native to web ──────────────────────────────────────────────────────────

type Handler<T extends NativeMessageType> = (
  data: Extract<NativeMessage, { type: T }>['data']
) => void;

/** Any handler, stored by the type it was registered for. */
type AnyHandler = (data: never) => void;

const listeners = new Map<NativeMessageType, Set<AnyHandler>>();

/**
 * Messages that arrived before anything listened for their type, oldest
 * first. The wrapper queues until `receive` is set, but `receive` is set once
 * for every type; a push tap delivered then would otherwise reach no router.
 * Held to the wrapper's own 50.
 */
let unheard: NativeMessage[] = [];
const UNHEARD_LIMIT = 50;

function deliver(message: NativeMessage) {
  const forType = listeners.get(message.type);
  if (!forType || forType.size === 0) {
    unheard = [...unheard, message].slice(-UNHEARD_LIMIT);
    return;
  }
  forType.forEach((handler) => handler(message.data as never));
}

let installedOn: RelayNative | null = null;

/**
 * Take `RelayNative.receive`, once. Called by `onNative`, so nothing has to
 * remember to start the bridge before listening.
 */
function install() {
  const relay = native();
  if (!relay || installedOn === relay) return;
  installedOn = relay;
  relay.receive = (message) => deliver(message as NativeMessage);
}

/**
 * Listen for one kind of message from the wrapper. Returns the unsubscribe.
 * Anything of this type that arrived before the first listener is delivered
 * now, in order. Does nothing in a browser, where no message ever comes.
 */
export function onNative<T extends NativeMessageType>(
  type: T,
  handler: Handler<T>
): () => void {
  install();
  const forType = listeners.get(type) ?? new Set();
  forType.add(handler as AnyHandler);
  listeners.set(type, forType);

  const waiting = unheard.filter((message) => message.type === type);
  if (waiting.length) {
    unheard = unheard.filter((message) => message.type !== type);
    waiting.forEach((message) => handler(message.data as never));
  }

  return () => {
    forType.delete(handler as AnyHandler);
  };
}

/**
 * A reply comes after its request, so one left over from an earlier request
 * nobody waited for is not this one's.
 */
function discardUnheard(types: NativeMessageType[]) {
  unheard = unheard.filter((message) => !types.includes(message.type));
}

/** The next message of one of `types`, for a request with one reply. */
function nextOf<T extends NativeMessageType>(
  types: T[]
): Promise<Extract<NativeMessage, { type: T }>> {
  discardUnheard(types);
  return new Promise((resolve) => {
    const stops: Array<() => void> = [];
    types.forEach((type) =>
      stops.push(
        onNative(type, (data) => {
          stops.forEach((stop) => stop());
          resolve({ type, data } as Extract<NativeMessage, { type: T }>);
        })
      )
    );
  });
}

// ─── Session ────────────────────────────────────────────────────────────────

/** How long a token refresh waits for the wrapper before giving up. */
export const TOKEN_REFRESH_TIMEOUT_MS = 10_000;

/**
 * A fresh access token from a v2 wrapper, which renews the session with
 * Auth0 natively; null in a browser, or when the wrapper does not answer in
 * time. The token fetcher re-read localStorage instead, which held the
 * token that had just expired, so a phone left open past its session got a
 * 401 on everything until the app went to the background and back.
 */
export function refreshNativeToken(
  timeoutMs = TOKEN_REFRESH_TIMEOUT_MS
): Promise<string | null> {
  if (!isNative()) return Promise.resolve(null);
  discardUnheard(['token']);
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      resolve(null);
    }, timeoutMs);
    const stop = onNative('token', ({ accessToken }) => {
      clearTimeout(timer);
      stop();
      resolve(accessToken);
    });
    send({ type: 'requestTokenRefresh' });
  });
}

// ─── Media ──────────────────────────────────────────────────────────────────

export interface PickedMedia {
  files: File[];
  /** What the wrapper left out, so a screen can say why: a video over 50 MB. */
  skipped: SkippedMedia[];
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
};

/** A wrapper's media item as the File the upload code takes. */
export function mediaItemToFile(item: MediaItem, index = 0): File {
  const bytes = Uint8Array.from(atob(item.base64), (char) =>
    char.charCodeAt(0)
  );
  const extension = EXTENSIONS[item.mimeType] ?? item.mimeType.split('/')[1];
  const stamp = item.capturedAt ? Date.parse(item.capturedAt) : Date.now();
  return new File([bytes], `media_${stamp}_${index}.${extension}`, {
    type: item.mimeType,
    lastModified: Number.isNaN(stamp) ? Date.now() : stamp,
  });
}

/** Collect one picker session: every `media` until `mediaComplete`. */
function collectMedia(): Promise<PickedMedia> {
  discardUnheard(['media', 'mediaComplete', 'mediaCancelled']);
  return new Promise((resolve) => {
    const items: MediaItem[] = [];
    const stops: Array<() => void> = [];
    const finish = (result: PickedMedia) => {
      stops.forEach((stop) => stop());
      resolve(result);
    };
    stops.push(
      onNative('media', ({ items: more }) => items.push(...more)),
      onNative('mediaComplete', ({ skipped }) =>
        finish({ files: items.map(mediaItemToFile), skipped })
      ),
      onNative('mediaCancelled', () => finish({ files: [], skipped: [] }))
    );
  });
}

/**
 * A browser's file input, the wrappers' fallback too. Resolves empty when
 * the person closes the picker, where the browser says so.
 */
function fileInput({
  accept,
  multiple,
  capture,
}: {
  accept: string;
  multiple: boolean;
  capture: boolean;
}): Promise<PickedMedia> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;
    if (capture) input.setAttribute('capture', 'environment');
    input.addEventListener('change', () =>
      resolve({ files: Array.from(input.files ?? []), skipped: [] })
    );
    input.addEventListener('cancel', () => resolve({ files: [], skipped: [] }));
    input.click();
  });
}

/**
 * Photos, and videos when the plan has them, from the camera or the library.
 * In a wrapper the native picker compresses photos and caps videos at the
 * upload limit, which a file input cannot.
 */
export function pickMedia({
  allowVideo,
}: {
  allowVideo: boolean;
}): Promise<PickedMedia> {
  if (isNative()) {
    const done = collectMedia();
    send({ type: 'openMediaPicker', data: { allowVideo } });
    return done;
  }
  return fileInput({
    accept: allowVideo ? 'image/*,video/*' : 'image/*',
    multiple: true,
    capture: false,
  });
}

/** One photo from the camera. */
export function capturePhoto(): Promise<PickedMedia> {
  if (isNative()) {
    const done = collectMedia();
    send({ type: 'capturePhoto' });
    return done;
  }
  return fileInput({ accept: 'image/*', multiple: false, capture: true });
}

// ─── Scanner ────────────────────────────────────────────────────────────────

/**
 * Whether Scan can open: the wrapper's own scanner, or a browser that can
 * open a camera (HTTPS and getUserMedia) for the page to read the code off.
 * ScanQrButton picks which.
 */
export const canScanQr = (): boolean => isNative() || canOpenCamera();

/**
 * The code the wrapper's scanner read, or null when it was closed. Null
 * straight away in a browser, whose scanner is the page's own camera.
 */
export async function scanQr(): Promise<string | null> {
  if (!isNative()) return null;
  const reply = nextOf(['qrScanned', 'scanCancelled']);
  send({ type: 'openScanner' });
  const message = await reply;
  return message.type === 'qrScanned' ? message.data.code : null;
}

// ─── Leaving the app ────────────────────────────────────────────────────────

/**
 * Open http(s), tel:, mailto: or sms: outside the app. A wrapper built
 * before v2 ignores a new window, so the page goes there itself: the old iOS
 * build sends another site to Safari, and the old Android build opens it in
 * place, which is how the update screen reaches the store from either.
 */
export function openExternal(url: string): void {
  if (send({ type: 'openExternal', data: { url } })) return;
  if (hasV1Bridge()) window.location.assign(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Put text on the clipboard; false when it could not be. Neither wrapper
 * has a copy message: the page's own Clipboard API works in WKWebView and
 * Android's WebView, as in a browser, when a tap asked for it.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Refused: no tap behind it, or the page lost focus.
    return false;
  }
}

export type ShareResult = 'shared' | 'copied' | 'failed';

/**
 * The system share sheet; in a browser without one, the link or text goes on
 * the clipboard so the caller can say "Copied".
 */
export async function share(content: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<ShareResult> {
  if (send({ type: 'share', data: content })) return 'shared';
  try {
    if (typeof navigator.share === 'function') {
      await navigator.share(content);
      return 'shared';
    }
    const copy = content.url ?? content.text;
    if (copy && (await copyText(copy))) return 'copied';
  } catch {
    // Closing the share sheet rejects too; nothing to report.
  }
  return 'failed';
}

// ─── Notifications ──────────────────────────────────────────────────────────

/** Whether this device will show Relay's notifications. */
export async function getNotificationPermission(): Promise<boolean> {
  if (isNative()) {
    const reply = nextOf(['notificationPermission']);
    send({ type: 'getNotificationPermission' });
    return (await reply).data.granted;
  }
  return typeof Notification !== 'undefined'
    ? Notification.permission === 'granted'
    : false;
}

/**
 * Ask to show notifications. Once someone has said no, neither a phone nor
 * a browser asks again; the answer is false at once and the screen should
 * offer the phone's settings.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (isNative()) {
    const reply = nextOf(['notificationPermission']);
    send({ type: 'requestNotificationPermission' });
    return (await reply).data.granted;
  }
  if (typeof Notification === 'undefined') return false;
  return (await Notification.requestPermission()) === 'granted';
}

/**
 * A test notification. In a wrapper the server pushes one to this device; a
 * browser shows one itself, which proves the permission if not the push.
 */
export function sendTestNotification(): void {
  if (send({ type: 'sendTestNotification' })) return;
  if (
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted'
  ) {
    new Notification('Relay', { body: 'Notifications are working.' });
  }
}

/** This app's page in the phone's Settings. False in a browser, which has none. */
export const openAppSettings = (): boolean => send({ type: 'openAppSettings' });

// ─── Session ────────────────────────────────────────────────────────────────

export type NativeSignInResult =
  | { signedIn: true }
  | { signedIn: false; cancelled: boolean; message?: string };

/**
 * Sign in through the wrapper, which opens Auth0 in the phone's own sign-in
 * sheet. Auth0's redirect inside the web view would not do: Google refuses
 * embedded web views, and the wrapper needs the session for its own
 * requests. Resolves when the wrapper hands over a token or says it did
 * not; there is no timeout, since the person may take minutes on the sheet.
 * Null in a browser.
 */
export function signInNative(
  how: NativeSignIn = {}
): Promise<NativeSignInResult> | null {
  if (!isNative()) return null;
  const reply = nextOf(['token', 'signInFailed']);
  send({ type: 'signIn', data: how });
  return reply.then((message) =>
    message.type === 'token'
      ? { signedIn: true }
      : { signedIn: false, ...message.data }
  );
}

/** Sign out in the wrapper. False in a browser, where Auth0 does it. */
export const signOutNative = (): boolean => send({ type: 'signOut' });

/**
 * Tell the wrapper which company is selected; it reloads into it, so the
 * page does not reload itself. False in a browser.
 */
export const setNativeCompany = (companyId: string): boolean =>
  send({ type: 'setCompany', data: { companyId } });

/** Test only: forget listeners, unheard messages and the installed receive. */
export function _resetNativeBridge(): void {
  listeners.clear();
  unheard = [];
  installedOn = null;
}
